import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  CheckSquare, Square, Plus, X, Link2, Ticket as TicketIcon, Calendar, ChevronDown, ChevronRight, ClipboardCheck,
} from 'lucide-react';
import { personalTasksApi } from '../api/personalTasks';
import { tasksApi } from '../api/tasks';
import { ticketsApi } from '../api/tickets';
import PageHeader from '../components/PageHeader';
import EmptyState from '../components/EmptyState';
import Modal from '../components/Modal';
import { StatusBadge, PriorityBadge } from '../components/badges';
import { useConfirm } from '../components/ConfirmDialog';
import { useTicketStatuses } from '../hooks/useTicketStatuses';
import TaskDetailModal from '../components/tickets/TaskDetailModal';
import { useAuth } from '../contexts/AuthContext';
import type { PersonalTask, MyTask } from '../types';

/* "My work": one page for the two kinds of task a person actually has —
   the sub-tickets other people are waiting on them for, and their own
   private list kept while building. The private half is visible to nobody
   else, ever. */

const errMsg = (e: any, fallback: string) => e?.response?.data?.message ?? e?.message ?? fallback;

const fmtDue = (iso?: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

/** Due today or already past — the only date styling worth shouting about. */
function isDueSoon(task: PersonalTask): boolean {
  if (task.status === 'completed' || !task.dueDate) return false;
  const due = new Date(task.dueDate).getTime();
  if (Number.isNaN(due)) return false;
  const end = new Date();
  end.setHours(23, 59, 59, 999);
  return due <= end.getTime();
}

// ─── Promote a private note into a real ticket ────────────────

function ToTicketModal({ task, onClose, onDone }: { task: PersonalTask; onClose: () => void; onDone: () => void }) {
  const [title, setTitle] = useState(task.title);
  const mutation = useMutation({
    mutationFn: () => personalTasksApi.toTicket(task.id, { title: title.trim() }),
    onSuccess: ({ ticket }) => { toast.success(`Created ${ticket.ticketNumber}`); onDone(); onClose(); },
    onError: (e: any) => toast.error(errMsg(e, 'Could not create the ticket')),
  });
  return (
    <Modal
      open
      onClose={onClose}
      busy={mutation.isPending}
      title="Create a ticket from this note"
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={mutation.isPending}>Cancel</button>
          <button type="button" className="btn btn-primary" disabled={!title.trim() || mutation.isPending} onClick={() => mutation.mutate()}>
            {mutation.isPending ? 'Creating…' : 'Create ticket'}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-muted">
          The ticket is assigned to you and starts with this note's details. Your list item stays, linked to it,
          so you can see where it went.
        </p>
        <div>
          <label className="label" htmlFor="to-ticket-title">Ticket title</label>
          <input id="to-ticket-title" autoFocus className="input" value={title} onChange={e => setTitle(e.target.value)} />
        </div>
      </div>
    </Modal>
  );
}

// ─── Link an existing ticket to a private note ────────────────

function LinkTicketModal({ task, onClose, onDone }: { task: PersonalTask; onClose: () => void; onDone: () => void }) {
  const [input, setInput] = useState('');
  const [found, setFound] = useState<Array<{ id: string; ticketNumber: string; title: string; status: string }>>([]);
  const lookup = useMutation({
    mutationFn: (numbers: string) => ticketsApi.lookupByNumbers(numbers),
    onSuccess: (rows) => { if (rows.length === 0) toast.error('No matching ticket found'); setFound(rows); },
    onError: (e: any) => toast.error(errMsg(e, 'Lookup failed')),
  });
  const link = useMutation({
    mutationFn: (linkedTicketId: string) => personalTasksApi.update(task.id, { linkedTicketId }),
    onSuccess: () => { toast.success('Ticket linked'); onDone(); onClose(); },
    onError: (e: any) => toast.error(errMsg(e, 'Could not link the ticket')),
  });
  return (
    <Modal
      open
      onClose={onClose}
      busy={link.isPending}
      title="Link a ticket"
      footer={<button type="button" className="btn btn-ghost" onClick={onClose}>Close</button>}
    >
      <div className="space-y-3">
        <form className="flex gap-2" onSubmit={e => { e.preventDefault(); if (input.trim()) lookup.mutate(input.trim()); }}>
          <input
            autoFocus
            className="input text-sm flex-1"
            placeholder="Ticket number, e.g. 213 or cTKT-00213"
            value={input}
            onChange={e => setInput(e.target.value)}
            aria-label="Ticket number to link"
          />
          <button type="submit" className="btn btn-secondary" disabled={!input.trim() || lookup.isPending}>
            {lookup.isPending ? 'Finding…' : 'Find'}
          </button>
        </form>
        {found.map(t => (
          <div key={t.id} className="flex items-center gap-2 text-sm">
            <span className="font-mono text-xs">{t.ticketNumber}</span>
            <span className="truncate flex-1">{t.title}</span>
            <StatusBadge status={t.status} />
            <button type="button" className="btn btn-primary text-xs py-1 px-2" onClick={() => link.mutate(t.id)} disabled={link.isPending}>
              Link
            </button>
          </div>
        ))}
      </div>
    </Modal>
  );
}

// ─── The page ─────────────────────────────────────────────────

export default function MyTasks() {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const { user } = useAuth();
  const myId: string | null = (user as any)?.id ?? (user as any)?._id ?? null;
  useTicketStatuses();   // feeds the badge registry with custom labels/colors

  const [newTitle, setNewTitle] = useState('');
  const [newDue, setNewDue] = useState('');
  const [showDone, setShowDone] = useState(false);
  const [toTicket, setToTicket] = useState<PersonalTask | null>(null);
  const [linking, setLinking] = useState<PersonalTask | null>(null);
  const [openTask, setOpenTask] = useState<MyTask | null>(null);

  const { data: personal, isLoading: personalLoading } = useQuery({
    queryKey: ['personal-tasks', 'all'],
    queryFn: () => personalTasksApi.list('all'),
  });
  const { data: assigned, isLoading: assignedLoading } = useQuery({
    queryKey: ['tasks', 'mine-page'],
    queryFn: () => tasksApi.mine('open'),
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['personal-tasks'] });
    qc.invalidateQueries({ queryKey: ['tasks'] });
  };
  const onError = (e: any) => toast.error(errMsg(e, 'Something went wrong'));

  const createMutation = useMutation({
    mutationFn: () => personalTasksApi.create({
      title: newTitle.trim(),
      ...(newDue ? { dueDate: new Date(`${newDue}T12:00:00`).toISOString() } : {}),
    }),
    onSuccess: () => { setNewTitle(''); setNewDue(''); invalidate(); },
    onError,
  });
  const toggleMutation = useMutation({
    mutationFn: (t: PersonalTask) => personalTasksApi.update(t.id, { status: t.status === 'completed' ? 'open' : 'completed' }),
    onSuccess: () => invalidate(),
    onError,
  });
  const unlinkMutation = useMutation({
    mutationFn: (t: PersonalTask) => personalTasksApi.update(t.id, { linkedTicketId: null }),
    onSuccess: () => { toast.success('Ticket unlinked'); invalidate(); },
    onError,
  });
  const deleteMutation = useMutation({
    mutationFn: (id: string) => personalTasksApi.remove(id),
    onSuccess: () => { toast.success('Removed from your list'); invalidate(); },
    onError,
  });

  const all = personal?.tasks ?? [];
  const open = all.filter(t => t.status !== 'completed');
  const done = all.filter(t => t.status === 'completed');
  const assignedTasks = assigned?.tasks ?? [];

  const submitNew = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTitle.trim()) return;
    createMutation.mutate();
  };

  const PersonalRow = ({ task }: { task: PersonalTask }) => {
    const finished = task.status === 'completed';
    const soon = isDueSoon(task);
    return (
      <li className="px-5 py-2.5 flex items-start gap-3">
        <button
          type="button"
          aria-label={finished ? `Reopen ${task.title}` : `Complete ${task.title}`}
          onClick={() => toggleMutation.mutate(task)}
          disabled={toggleMutation.isPending}
          className="mt-0.5 shrink-0"
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', lineHeight: 0 }}
        >
          {finished
            ? <CheckSquare className="w-4 h-4" style={{ color: 'var(--scpa-status-ok)' }} />
            : <Square className="w-4 h-4 text-muted" />}
        </button>
        <div className="flex-1 min-w-0">
          <span className={`text-sm ${finished ? 'line-through text-muted' : 'text-fg font-medium'}`}>{task.title}</span>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted mt-0.5">
            {task.dueDate && (
              <span
                className={soon ? 'font-semibold' : ''}
                style={soon ? { color: 'var(--scpa-status-warning)' } : undefined}
              >
                <Calendar className="w-3 h-3 inline mr-1" />
                {fmtDue(task.dueDate)}
              </span>
            )}
            {task.linkedTicket && (
              <Link to={`/tickets/${task.linkedTicket.id}`} className="inline-flex items-center gap-1 text-primary hover:underline" title={task.linkedTicket.title}>
                <Link2 className="w-3 h-3" /> {task.linkedTicket.ticketNumber}
                <StatusBadge status={task.linkedTicket.status} />
              </Link>
            )}
          </div>
        </div>
        {!finished && !task.linkedTicket && (
          <>
            <button type="button" className="btn btn-ghost text-xs py-1 px-2 shrink-0" onClick={() => setToTicket(task)} title="Turn this into a real ticket">
              <TicketIcon className="w-3.5 h-3.5" /> Make ticket
            </button>
            <button type="button" className="btn btn-ghost text-xs py-1 px-2 shrink-0" onClick={() => setLinking(task)} title="Link an existing ticket">
              <Link2 className="w-3.5 h-3.5" />
            </button>
          </>
        )}
        {task.linkedTicket && (
          <button type="button" className="btn btn-ghost text-xs py-1 px-2 shrink-0" onClick={() => unlinkMutation.mutate(task)} title="Unlink the ticket">
            <Link2 className="w-3.5 h-3.5" /> Unlink
          </button>
        )}
        <button
          type="button"
          className="text-muted hover:text-err shrink-0 mt-1"
          title={`Remove "${task.title}"`}
          aria-label={`Remove ${task.title}`}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, lineHeight: 0 }}
          onClick={async () => {
            if (await confirm({ title: 'Remove this item?', message: `"${task.title}" is deleted from your list.`, confirmLabel: 'Remove', danger: true })) {
              deleteMutation.mutate(task.id);
            }
          }}
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </li>
    );
  };

  return (
    <div className="flex flex-col gap-4 pb-10">
      <PageHeader
        title="My work"
        intro="Tasks other people are waiting on you for, and your own private list."
      />

      {/* Sub-ticket tasks assigned to me */}
      <div className="card overflow-hidden" style={{ padding: 0 }}>
        <div className="px-5 py-3.5 border-b border-line-light flex items-center justify-between gap-2">
          <h2 className="section-title text-base flex items-center gap-2">
            <ClipboardCheck className="w-4 h-4 text-muted" /> Assigned to me on tickets
            {assignedTasks.length > 0 && <span className="text-xs text-muted font-normal">{assignedTasks.length} open</span>}
          </h2>
          <Link to="/tickets?taskForMe=1" className="text-xs text-primary hover:underline">See the tickets</Link>
        </div>
        {assignedLoading ? (
          <p className="px-5 py-4 text-xs text-muted">Loading…</p>
        ) : assignedTasks.length === 0 ? (
          <p className="px-5 py-4 text-xs text-muted">Nothing assigned to you right now.</p>
        ) : (
          <ul className="divide-y divide-[color:var(--scpa-border-light)]">
            {assignedTasks.map(task => (
              <li key={task.id} className="px-5 py-2.5 flex items-center gap-3">
                <ClipboardCheck className="w-4 h-4 shrink-0" style={{ color: 'var(--scpa-status-warning)' }} />
                <button
                  type="button"
                  onClick={() => setOpenTask(task)}
                  className="flex-1 min-w-0 text-left"
                  style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                >
                  <span className="block text-sm font-medium text-fg truncate">{task.title}</span>
                  <span className="block text-[11px] text-muted truncate">
                    <span className="font-mono font-bold text-primary mr-1.5">{task.ticket.ticketNumber}</span>
                    {task.ticket.title}
                  </span>
                </button>
                <PriorityBadge priority={task.ticket.priority} />
                {task.canOpenTicket && (
                  <Link to={`/tickets/${task.ticket.id}`} className="text-xs text-primary hover:underline shrink-0">Open ticket</Link>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* My own list */}
      <div className="card overflow-hidden" style={{ padding: 0 }}>
        <div className="px-5 py-3.5 border-b border-line-light">
          <h2 className="section-title text-base">My list</h2>
          <p className="text-[11px] text-muted mt-0.5">Private to you. Nobody else can see these, not even an admin.</p>
        </div>

        <form onSubmit={submitNew} className="px-5 py-3 border-b border-line-light flex items-center gap-2 flex-wrap">
          <input
            className="input text-sm flex-1 min-w-[14rem]"
            placeholder="What do you need to remember?"
            aria-label="New item"
            value={newTitle}
            onChange={e => setNewTitle(e.target.value)}
          />
          <input
            type="date"
            className="input text-sm w-auto"
            aria-label="Due date (optional)"
            value={newDue}
            onChange={e => setNewDue(e.target.value)}
          />
          <button type="submit" className="btn btn-primary" disabled={!newTitle.trim() || createMutation.isPending}>
            <Plus className="w-3.5 h-3.5" /> {createMutation.isPending ? 'Adding…' : 'Add'}
          </button>
        </form>

        {personalLoading ? (
          <p className="px-5 py-4 text-xs text-muted">Loading…</p>
        ) : all.length === 0 ? (
          <EmptyState>Your list is empty. Jot down anything you need to come back to.</EmptyState>
        ) : (
          <>
            {open.length > 0 && (
              <ul className="divide-y divide-[color:var(--scpa-border-light)]">
                {open.map(task => <PersonalRow key={task.id} task={task} />)}
              </ul>
            )}
            {done.length > 0 && (
              <div className={open.length > 0 ? 'border-t border-line-light' : ''}>
                <button
                  type="button"
                  className="w-full text-left px-5 py-2 text-xs font-semibold text-muted hover:text-fg inline-flex items-center gap-1"
                  style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                  onClick={() => setShowDone(v => !v)}
                >
                  {showDone ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                  {done.length} done
                </button>
                {showDone && (
                  <ul className="divide-y divide-[color:var(--scpa-border-light)] border-t border-line-light">
                    {done.map(task => <PersonalRow key={task.id} task={task} />)}
                  </ul>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {toTicket && <ToTicketModal task={toTicket} onClose={() => setToTicket(null)} onDone={invalidate} />}
      {linking && <LinkTicketModal task={linking} onClose={() => setLinking(null)} onDone={invalidate} />}
      {openTask && (
        <TaskDetailModal
          ticketId={openTask.ticketId}
          taskId={openTask.id}
          members={[]}
          myId={myId}
          onClose={() => setOpenTask(null)}
          onChanged={invalidate}
        />
      )}
    </div>
  );
}
